import {setLicenseText} from "@luciad/ria/util/License.js";

//  tslint:disable-next-line:no-var-requires
setLicenseText(require("./license/luciadria-license.txt"));

import {LocaleSetup, setupMomentIntl, setupReactIntl} from "./common/intl/intl";

setupMomentIntl(); // has to be done before other modules are imported

import axios from "axios";
import {SERVER_CONFIG_PATH} from "./paths";
import {createHashHistory} from "history";
import {stringify} from "query-string";
import * as React from "react";
import * as ReactDOM from "react-dom";
import {defineMessages, IntlProvider} from "react-intl";
import {Provider} from "react-redux";
import {HashRouter} from "react-router-dom";
import {applyMiddleware, createStore, Store} from "redux";
import thunk from "redux-thunk";
import {TextDecoder, TextEncoder} from "text-encoding";
import {createApolloApi} from "./api/ApolloApi";
import {createUserTourApi} from "./api/UserTourInternalApi";
import "./app.scss";
import {selectors as toursSelectors} from "./common/ui/tour/selectors";
import {rootReducer} from "./rootReducer";
import {renderRoutes} from "./routes";
import Startup from "./startup/startup";
import {selectors as userSelectors} from "./user/selectors";
import {startOidcProvider} from "./oidc/startOidcProvider";
import {OidcProviderInfo, ServerConfiguration} from "./model";
import {initializeUserManager, userManager} from "./oidc/utils/userManager";
import {OidcProvider} from "redux-oidc";
import {Api} from "./api/InternalApi";
import {ErrorDisplay} from "./common/ui/errordisplay/ErrorDisplay";

// Set up text encoding polyfill if necessary (only needed for MS Edge)
if (!window["TextEncoder"]) {
  window["TextEncoder"] = TextEncoder;
  window["TextDecoder"] = TextDecoder;
}

const rootEl = document.getElementById("root");

const reduxdevtools: any = window["devToolsExtension"] ? window["devToolsExtension"]() : (f: any) => f;

const getApi = (state): Api => {
  return createApi(!userSelectors.getUserTourCompleted(state) || !toursSelectors.getStyleDataTourCompleted(state));
}

export const createApi = (userTourApi: boolean) => {
  if (!_serverConfiguration) {
    throw new Error("ServerConfiguration not loaded");
  }

  if (userTourApi) {
    return createUserTourApi(_serverConfiguration);
  }

  const apolloApi = createApolloApi(_serverConfiguration);
  apolloApi.addApiErrorHandler(error401Handler);
  return apolloApi;
}

const middlewares = [thunk.withExtraArgument({getApi})];

// enable logging of redux actions for the development server
if (process.env.NODE_ENV === 'development') {
  // tslint:disable-next-line:no-var-requires
  const {logger} = require('redux-logger');
  middlewares.push(logger);
}

//ControlRoom uses a HashRouter (urls in the form of /studio/#/services/1).
//The advantage of this over BrowserHistory ('cleaner' urls like /studio/services/1) is that the server hosting
//ControlRoom needs no special configuration. With a BrowserHistory, the server needs to
//serve index.html for every client-side path (e.g. studio/services/1 and studio/products/2).
//Using BrowserHistory, ControlRoom also needs to be aware of the 'base' path it is deployed on. This is not the case
//for HashRouter. Because of this, it's harder to deploy ControlRoom on different server paths and/or behind
//reverse proxies when using BrowserHistory.
//The only downsides of HashRouter compared to BrowserHistory are that URLs are less pretty (there's a hash/anchor)
//inside them) and server-side rendering is not supported (we don't do this anyway).
const history = createHashHistory();
const LOGIN_PAGE_MESSAGES = defineMessages({
  pleaseAuthenticate: {
    id: "studio.login.please-authenticate",
    defaultMessage: "Please Authenticate",
  },
});
const error401Handler = (error) => {
  if (error?.response && error.response.status === 401) {
    //multiple 401's can come in quick succession, make sure the redirect only happens once!
    const alreadyRedirected = history.location.pathname.indexOf("login") >= 0;
    if (!alreadyRedirected) {
      history.push({
        pathname: "/login",
        search: stringify({
          redirect: history.location.pathname,
          messageId: LOGIN_PAGE_MESSAGES.pleaseAuthenticate.id,
          defaultMessage: LOGIN_PAGE_MESSAGES.pleaseAuthenticate.defaultMessage,
        }),
      });
    }
  }
};

let _serverConfiguration: ServerConfiguration;
const loadServerConfiguration = (): Promise<ServerConfiguration> => {
  return axios.get(SERVER_CONFIG_PATH)
      .then(response => response.data as ServerConfiguration)
      .catch(error => {
        const connectionError: Error = new Error(
            "Failed to acquire LuciadFusion server configuration: " + error.message);
        connectionError.stack = error.stack;
        throw connectionError
      });
}

let store: Store<any> = null;
loadServerConfiguration().then((serverConfiguration) => {
  _serverConfiguration = serverConfiguration;
  startOidcProvider(_serverConfiguration.studio.webApp.basePath).then((oidcProviderInfo: OidcProviderInfo) => {
    if (oidcProviderInfo?.enabled) {
      store = initializeUserManager(oidcProviderInfo, getApi)
    } else {
      const middleware = applyMiddleware(thunk.withExtraArgument({getApi}));
      //Use composeWithDevTools for debug environment to view redux state;however, do not use in production
      //add import {composeWithDevTools} from "redux-devtools-extension";
      //store = createStore(rootReducer, {}, composeWithDevTools(middleware));
      store = createStore(rootReducer, {}, middleware);
    }

    const renderApp = (result: LocaleSetup) => {
      ReactDOM.render((
          <IntlProvider locale={result.locale} messages={result.messages}>
            <Provider store={store}>
              {oidcProviderInfo?.enabled ?
               <OidcProvider userManager={userManager} store={store}>
                 <HashRouter>
                   {renderRoutes()}
                 </HashRouter>
               </OidcProvider> :
               <Startup history={history}>
                 <HashRouter>
                   {renderRoutes()}
                 </HashRouter>
               </Startup>
              }
            </Provider>
          </IntlProvider>
      ), rootEl);
    };
    Promise.all([setupReactIntl()])
        .then(([localeSetup]) => {
          renderApp(localeSetup as LocaleSetup);
        }).catch(error => renderError(error));
  }).catch(error => renderError(error));
}).catch((error) => {
  throw new Error("Failed to find configuration settings. : " + error);
});

const renderError = (error: Error) => {
  ReactDOM.render((
      <IntlProvider>
        <ErrorDisplay error={error}/>
      </IntlProvider>
  ), rootEl);
}